/**
 * Level 3, Lessons 7–8 skills: arrays (counted by columns; a × b = a counted b times — Meliesa, Oct 29, so six
 * columns of 4 is 4 × 6), turnarounds, equal groups, missing factors ("count your way to the answer — the number of
 * counts IS the missing number"), missing numbers in + and −, and the multiplication–division fact family.
 */
import { int, pick } from "../core";
import type { Item, Skill } from "../types";
import { dotArray } from "../visuals";

const hit = (raw: string, n: number) => raw.trim().replace(/,/g, "") === String(n);
const TABLES = [3, 4, 6, 7, 8, 9, 12];

/* Arrays: (in each column) × (columns) = product. */
export function arrayItem(cols: number, per: number): Item {
  const ans = cols * per;
  return {
    id: `array-times-fact:${cols}x${per}`,
    skill: "array-times-fact",
    prompt: "Write the array as a times fact, then solve. (In each column × how many columns.)",
    visual: dotArray(cols, per),
    input: { kind: "fields", fields: [{ width: 2 }, { label: "×", width: 2 }, { label: "=", width: 3 }] },
    answer: `${per}|${cols}|${ans}`,
    diagnose: (raw) => {
      const [a, b, c] = raw.split("|").map(Number);
      if (cols !== per && a === cols && b === per && c === ans) return { code: "array-order", hint: `Same dots, same answer — but read it the way we count: ${per}, counted ${cols} times, is ${per} × ${cols}.` };
      if (a === per && b === cols && c !== ans) return { code: "array-count-slip", hint: `The fact is right. Count by the column: ${per}, ${per * 2}, ${per * 3}…` };
      if (c === cols + per && c !== ans) return { code: "array-added", hint: "Times means how many times you counted. Count by the column, don't add the two numbers." };
      return null;
    },
    hints: ["How many dots in each column? How many columns?", `${per} in each column, ${cols} columns: ${per}, counted ${cols} times.`, `${per} × ${cols} = ${ans}. Count by the column: ${Array.from({ length: cols }, (_, i) => per * (i + 1)).join(", ")}.`],
  };
}
export const arrays: Skill = {
  id: "array-times-fact",
  name: "Arrays as times facts",
  gen: (rng) => arrayItem(int(rng, 2, 9), int(rng, 2, 6)),
};

export function turnItem(a: number, b: number, form: number): Item {
  const forms = [
    { p: `${a} × ${b} = ${b} × __`, ans: a },
    { p: `${a} × __ = ${b} × ${a}`, ans: b },
    { p: `${a} × ${b} = __ × ${a}`, ans: b },
  ];
  const f = forms[form % 3]!;
  return {
    id: `turnaround:${f.p}`,
    skill: "turnaround",
    prompt: `Turnaround — fill it in without counting: &nbsp; ${f.p}`,
    input: { kind: "number" },
    answer: String(f.ans),
    diagnose: (raw) => (hit(raw, a * b) ? { code: "turn-product", hint: "Not the answer — the missing factor. Turning an array never changes it: the same two numbers trade places." } : null),
    hints: ["What did the arrays teach you about turning?", "The same two numbers, trading places.", `${a} × ${b} = ${b} × ${a}, so the missing number is ${f.ans}.`],
  };
}
export const turnaround: Skill = { id: "turnaround", name: "Turnaround facts", gen: (rng) => turnItem(pick(rng, TABLES), int(rng, 2, 12), int(rng, 0, 2)) };

export function missingFactorItem(t: number, k: number, form: number): Item {
  const p = t * k;
  const forms = [`${t} × __ = ${p}`, `__ × ${t} = ${p}`, `${p} = ${t} × __`];
  const f = forms[form % 3]!;
  return {
    id: `missing-factor:${f}`,
    skill: "missing-factor",
    prompt: f,
    input: { kind: "number" },
    answer: String(k),
    diagnose: (raw) => {
      if (hit(raw, p)) return { code: "missing-gave-product", hint: "The answer is already there. Find how many times you count." };
      if (hit(raw, p - t)) return { code: "missing-subtracted", hint: `Not take away — count by ${t}s to ${p}. The number of counts is the missing number.` };
      if (hit(raw, k + 1) || hit(raw, k - 1)) return { code: "missing-count-slip", hint: `Count again, by ${t}s, from the nearest anchor.` };
      return null;
    },
    hints: [`You know the ${t}s. Dial the code and count by ${t}s to ${p}.`, `The number of counts IS the missing number. Jump to the nearest anchor (${t} × 5 = ${t * 5}, ${t} × 10 = ${t * 10}) and count the short way.`, `${Array.from({ length: k }, (_, i) => t * (i + 1)).join(", ")}: ${k} counts. The missing number is ${k}.`],
  };
}
export const missingFactor: Skill = { id: "missing-factor", name: "Missing factors", gen: (rng) => missingFactorItem(pick(rng, TABLES), int(rng, 2, 12), int(rng, 0, 2)) };

export function missingAddSubItem(form: number, a: number, b: number): Item {
  // forms: __ = a + b ; a + __ = s ; __ − b = d ; a − __ = d (within 20)
  let p = "", ans = 0;
  if (form === 0) { p = `__ = ${a} + ${b}`; ans = a + b; }
  else if (form === 1) { p = `${a} + __ = ${a + b}`; ans = b; }
  else if (form === 2) { p = `__ − ${b} = ${a}`; ans = a + b; }
  else { p = `${a + b} − __ = ${a}`; ans = b; }
  return {
    id: `missing-add-sub:${p}`,
    skill: "missing-add-sub",
    prompt: p,
    input: { kind: "number" },
    answer: String(ans),
    diagnose: (raw) => (form === 1 && hit(raw, a + a + b) ? { code: "missing-added-both", hint: "The total is already given. What do you add to get there?" } : form === 2 && hit(raw, Math.abs(a - b)) ? { code: "missing-subtracted", hint: `Something take away ${b} leaves ${a}. Count up from ${a}.` } : null),
    hints: ["Count from what you know.", form % 2 === 0 ? `Put the two you know together.` : `Count up from the smaller number to the bigger one.`, `${p.replace("__", String(ans))}.`],
  };
}
export const missingAddSub: Skill = {
  id: "missing-add-sub",
  name: "Missing numbers in + and −",
  gen: (rng) => { const a = int(rng, 3, 12), b = int(rng, 2, 9); return missingAddSubItem(int(rng, 0, 3), a, Math.min(b, 20 - a)); },
};

export function equalGroupsItem(groups: number, each: number, story: number): Item {
  const ans = groups * each;
  const s = [
    `A classroom has ${groups} rows of desks with ${each} desks in each row. How many desks?`,
    `Mia bakes ${groups} trays of muffins, ${each} muffins on each tray. How many muffins?`,
    `${groups} teams with ${each} players on each team. How many players?`,
    `${groups} boxes of crayons, ${each} crayons in each box. How many crayons?`,
  ][story % 4]!;
  return {
    id: `equal-groups-word:${groups}x${each}:${story % 4}`,
    skill: "equal-groups-word",
    prompt: `${s} Write the times fact, then solve.`,
    input: { kind: "fields", fields: [{ width: 2 }, { label: "×", width: 2 }, { label: "=", width: 3 }] },
    answer: `${each}|${groups}|${ans}`,
    alsoAccept: [`${groups}|${each}|${ans}`],
    diagnose: (raw) => (raw.split("|")[2] === String(groups + each) ? { code: "array-added", hint: "A word problem is an array in disguise: the groups are the columns. Count by the group." } : null),
    hints: ["A word problem is an array in disguise: the groups are the columns.", `${each} in each group, ${groups} groups: ${each}, counted ${groups} times.`, `${each} × ${groups} = ${ans}.`],
  };
}
export const equalGroups: Skill = { id: "equal-groups-word", name: "Equal groups", gen: (rng) => equalGroupsItem(int(rng, 3, 9), pick(rng, [3, 4, 6, 7, 8, 12]), int(rng, 0, 3)) };

/* L3.8 The fact family of four: a × b, b × a, ab ÷ a, ab ÷ b. */
export function familyItem(a: number, b: number): Item {
  const p = a * b;
  return {
    id: `fact-family-mult-div:${a}x${b}`,
    skill: "fact-family-mult-div",
    prompt: `Every times fact is a family of four. Write the whole family for ${a} × ${b} = ${p}:`,
    input: { kind: "fields", fields: [{ label: `${a} × ${b} = ${p};` }, { label: "×" }, { label: "=", width: 3 }, { label: ";", width: 3 }, { label: "÷" }, { label: "=" }, { label: ";", width: 3 }, { label: "÷" }, { label: "=" }] },
    answer: `${b}|${a}|${p}|${p}|${a}|${b}|${p}|${b}|${a}`,
    alsoAccept: [`${b}|${a}|${p}|${p}|${b}|${a}|${p}|${a}|${b}`],
    hints: ["The turnaround first: the same two numbers, trading places.", `Division asks the question backwards: how many ${a}s make ${p}? How many ${b}s make ${p}?`, `${b} × ${a} = ${p}; ${p} ÷ ${a} = ${b}; ${p} ÷ ${b} = ${a}.`],
  };
}
export const factFamily: Skill = { id: "fact-family-mult-div", name: "Fact families", gen: (rng) => { const a = pick(rng, TABLES); let b = int(rng, 2, 9); if (b === a) b++; return familyItem(a, b); } };

export const LEVEL3_L7TO8_SKILLS: Skill[] = [arrays, turnaround, missingFactor, missingAddSub, equalGroups, factFamily];
